import { useMemo, useState } from 'react';
import { CalendarClock, CheckCircle2, Flame, Inbox } from 'lucide-react';

import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { AnimatePresence } from 'motion/react';

import { useCurrentUser } from '@/features/auth/useAuth';
import {
  BACKLOG_SORTS,
  COMPLETED_BACKLOG_SORTS,
  groupBacklog,
  type BacklogSort,
} from '@/features/calendars/backlogSort';
import { QuickAdd } from './QuickAdd';
import { TaskDetail } from './TaskDetail';
import { TaskRow } from './TaskRow';
import { buildTree, formatDuration } from './tree';
import type { TaskNode, TaskStatus } from './types';
import { useClients, useProjects, useTask, useTasks, useToggleDone } from './useTasks';

/** Open work not yet on the calendar. */
const OPEN_STATUSES: TaskStatus[] = ['inbox', 'backlog', 'in_progress'];

/** On the plan already, excluded from the open backlog the same way Plan/Daily do. */
const SCHEDULED_STATUSES: TaskStatus[] = ['scheduled'];

type BacklogView = 'open' | 'scheduled' | 'completed';

/** Matches the server's `max_estimate_minutes` filter. */
const QUICK_WIN_MINUTES = 15;

const SORT_KEY = 'chronos-backlog-sort';
const SCHEDULED_SORT_KEY = 'chronos-backlog-scheduled-sort';
const COMPLETED_SORT_KEY = 'chronos-backlog-completed-sort';

const OPEN_SORTS = new Set<BacklogSort>(BACKLOG_SORTS.map((s) => s.value));
const COMPLETED_SORTS = new Set<BacklogSort>(COMPLETED_BACKLOG_SORTS.map((s) => s.value));

function sortKeyFor(view: BacklogView): string {
  if (view === 'completed') return COMPLETED_SORT_KEY;
  if (view === 'scheduled') return SCHEDULED_SORT_KEY;
  return SORT_KEY;
}

function loadSort(view: BacklogView): BacklogSort {
  const saved = localStorage.getItem(sortKeyFor(view));
  const allowed = view === 'completed' ? COMPLETED_SORTS : OPEN_SORTS;
  return saved && allowed.has(saved as BacklogSort)
    ? (saved as BacklogSort)
    : view === 'completed'
      ? 'completed'
      : 'due';
}

function viewTitle(view: BacklogView): string {
  if (view === 'completed') return 'Completed';
  if (view === 'scheduled') return 'Scheduled';
  return 'Backlog';
}

export function BacklogPage() {
  const [clientId, setClientId] = useState<number | undefined>();
  const [quickWins, setQuickWins] = useState(false);
  const [view, setView] = useState<BacklogView>('open');
  const [sort, setSort] = useState<BacklogSort>(() => loadSort('open'));
  const [openTaskId, setOpenTaskId] = useState<number | null>(null);

  const clients = useClients();
  const projects = useProjects();
  const { data: user } = useCurrentUser();
  const timezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // One unfiltered query, filtered in the browser. The backlog is a couple of
  // hundred rows at most, and doing it here buys instant chips, live per-client
  // counts, and no skeleton flash on every tap.
  const tasks = useTasks(
    view === 'completed'
      ? { completed_history: true, sort: 'completed_at_desc' }
      : view === 'scheduled'
        ? { status_in: SCHEDULED_STATUSES }
        : { status_in: OPEN_STATUSES },
  );
  const toggleDone = useToggleDone();

  const sortOptions = view === 'completed' ? COMPLETED_BACKLOG_SORTS : BACKLOG_SORTS;
  const effectiveSort = sortOptions.some((s) => s.value === sort)
    ? sort
    : view === 'completed'
      ? 'completed'
      : 'due';

  const all = useMemo(() => tasks.data ?? [], [tasks.data]);

  const countsByClient = useMemo(() => {
    const counts = new Map<number, number>();
    for (const task of all) counts.set(task.client_id, (counts.get(task.client_id) ?? 0) + 1);
    return counts;
  }, [all]);

  // Quick-wins only applies to the open backlog. Completed/scheduled must not
  // AND with a false short-circuit, that emptied the list while chips still
  // counted from `all`.
  const visible = useMemo(
    () =>
      all.filter(
        (t) =>
          (clientId === undefined || t.client_id === clientId) &&
          (view !== 'open' ||
            !quickWins ||
            (t.estimate_minutes !== null && t.estimate_minutes <= QUICK_WIN_MINUTES)),
      ),
    [all, clientId, quickWins, view],
  );

  const tree = useMemo(() => buildTree(visible), [visible]);

  const groups = useMemo(() => {
    const byId = new Map(tree.map((node) => [node.id, node]));
    // Group the roots, then re-attach their subtrees. The grouper is the same
    // one the Plan sidebar uses, so "Overdue" means the same thing on both.
    return groupBacklog(tree, effectiveSort, {
      timezone,
      clients: clients.data ?? [],
      projects: projects.data ?? [],
    }).map((group) => {
      const nodes = group.tasks
        .map((t) => byId.get(t.id))
        .filter((n): n is TaskNode => n !== undefined);
      // Rollups, not own estimates: a group header that ignores subtasks
      // undercounts exactly the branches that need planning most.
      const count = nodes.reduce((sum, n) => sum + n.rollup_total_count, 0);
      const minutes = nodes.reduce((sum, n) => sum + n.rollup_estimate_minutes, 0);
      const total = formatDuration(minutes);
      return {
        ...group,
        nodes,
        meta: total ? `${count} · ${total}` : `${count}`,
      };
    });
  }, [tree, effectiveSort, timezone, clients.data, projects.data]);

  // Read from the list rather than holding a copy, so the sheet reflects
  // server state after every autosave instead of drifting from it. A subtask
  // opened from its parent may be done or deferred, and so absent from this
  // list -- that one is fetched by id rather than silently closing the sheet.
  const listed = all.find((t) => t.id === openTaskId) ?? null;
  const fetched = useTask(listed || openTaskId == null ? null : openTaskId);
  const openTask = listed ?? fetched.data ?? null;
  const openNode = useMemo(
    () => (openTask ? { ...openTask, children: [] } : null),
    [openTask],
  );

  const showSkeleton = tasks.isPending && !tasks.data;
  const showHardError = tasks.isError && !tasks.data;

  const colorFor = (id: number) =>
    clients.data?.find((c) => c.id === id)?.color ?? 'var(--border)';

  const rootCount = tree.length;
  const taskCount = tree.reduce((sum, n) => sum + n.rollup_total_count, 0);
  const totalMinutes = tree.reduce((sum, n) => sum + n.rollup_estimate_minutes, 0);
  const totalLabel = formatDuration(totalMinutes);

  // Clients with work come first: a chip that leads nowhere should not sit
  // between two that do.
  const clientChips = useMemo(() => {
    const list = [...(clients.data ?? [])];
    list.sort((a, b) => {
      const diff = (countsByClient.get(b.id) ?? 0) - (countsByClient.get(a.id) ?? 0);
      return diff !== 0 ? diff : a.name.localeCompare(b.name);
    });
    return list;
  }, [clients.data, countsByClient]);

  const changeSort = (next: BacklogSort) => {
    setSort(next);
    localStorage.setItem(sortKeyFor(view), next);
  };

  const setBacklogView = (next: BacklogView) => {
    setView(next);
    setQuickWins(false);
    setSort(loadSort(next));
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-5 py-6">
      {/* Title and volume on one line: the number is a property of the page,
          not an announcement that deserves its own paragraph. */}
      <header className="mb-4 flex items-baseline justify-between gap-3">
        <h1 className="text-title font-semibold">{viewTitle(view)}</h1>
        {taskCount > 0 && (
          <p className="shrink-0 text-small text-text-secondary">
            <span className="tabular">{taskCount}</span>
            {taskCount === 1 ? ' task' : ' tasks'}
            {totalLabel && (
              <>
                {' · '}
                <span className="tabular">{totalLabel}</span>
              </>
            )}
          </p>
        )}
      </header>

      {view === 'open' && (
        <div className="mb-3">
          <QuickAdd defaultClientId={clientId} />
        </div>
      )}

      <div
        role="group"
        aria-label="Backlog view"
        className="mb-3 flex flex-wrap gap-1.5"
      >
        <ViewChip
          active={view === 'open'}
          onClick={() => setBacklogView('open')}
          icon={<Inbox size={14} strokeWidth={1.5} aria-hidden />}
        >
          Open
        </ViewChip>
        <ViewChip
          active={view === 'scheduled'}
          onClick={() => setBacklogView('scheduled')}
          icon={<CalendarClock size={14} strokeWidth={1.5} aria-hidden />}
        >
          Scheduled
        </ViewChip>
        <ViewChip
          active={view === 'completed'}
          onClick={() => setBacklogView('completed')}
          icon={<CheckCircle2 size={14} strokeWidth={1.5} aria-hidden />}
        >
          Completed
        </ViewChip>
      </div>

      {/* One scrolling row, never a wrapping block. Client count is unbounded;
          page height should not be. */}
      <div
        role="group"
        aria-label="Filter by client"
        className="-mx-5 overflow-x-auto px-5 [mask-image:linear-gradient(to_right,transparent,#000_0.75rem,#000_calc(100%-1.25rem),transparent)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="flex w-max gap-1.5 pb-1">
          <FilterChip
            active={clientId === undefined}
            onClick={() => setClientId(undefined)}
            count={all.length}
          >
            All
          </FilterChip>
          {clientChips.map((client) => (
            <FilterChip
              key={client.id}
              active={clientId === client.id}
              onClick={() => setClientId(client.id)}
              dot={client.color}
              count={countsByClient.get(client.id) ?? 0}
            >
              {client.name}
            </FilterChip>
          ))}
        </div>
      </div>

      {/* View controls, not filters-by-another-name. Keeping "Quick wins" out
          of the client row stops it reading as one more client. */}
      <div className="mb-4 mt-2 flex items-center justify-between gap-3 border-t border-border pt-2">
        <label className="flex min-w-0 items-center gap-2 text-small text-text-secondary">
          <span className="shrink-0">Group by</span>
          <select
            value={effectiveSort}
            onChange={(e) => changeSort(e.target.value as BacklogSort)}
            className={cn(
              'min-w-0 rounded border border-border bg-surface-1 py-1 pl-2 pr-1',
              'text-small text-text outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border',
            )}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        {view === 'open' && (
          <button
            type="button"
            onClick={() => setQuickWins((v) => !v)}
            aria-pressed={quickWins}
            className={cn(
              'pressable inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1',
              'text-small transition-colors duration-press',
              quickWins
                ? 'border-accent bg-accent text-text-on-accent'
                : 'border-border text-text-secondary hover:bg-surface-2 hover:text-text',
            )}
          >
            <Flame size={13} strokeWidth={1.5} aria-hidden />
            Quick wins
          </button>
        )}
      </div>

      {showSkeleton && (
        // Skeleton rather than a spinner: it holds the layout so nothing jumps
        // when the real rows arrive.
        <ul className="space-y-2" aria-hidden>
          {[0, 1, 2].map((i) => (
            <li key={i} className="h-11 animate-pulse rounded bg-surface-2" />
          ))}
        </ul>
      )}

      {showHardError && (
        <p role="alert" className="text-body text-danger">
          Could not load tasks.{' '}
          <button className="underline" onClick={() => tasks.refetch()}>
            Retry
          </button>
        </p>
      )}

      {tasks.data && rootCount === 0 && !showSkeleton && (
        <EmptyState
          view={view}
          quickWins={quickWins}
          clientFiltered={clientId !== undefined}
          onClear={() => {
            setQuickWins(false);
            setClientId(undefined);
          }}
        />
      )}

      {groups.map((group) => (
        <section key={group.key} className="mb-4 last:mb-0" aria-label={group.label}>
          {/* A single group needs no header: the label would only restate the
              filter the user just set. */}
          {groups.length > 1 && (
            <h2
              className={cn(
                'mb-1 flex items-baseline justify-between gap-2 px-0.5',
                'text-label font-medium',
                group.tone === 'overdue' && 'text-danger',
                group.tone === 'today' && 'text-warning',
                group.tone !== 'overdue' && group.tone !== 'today' && 'text-text-secondary',
              )}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                {group.color && (
                  <span
                    aria-hidden
                    className="size-dot shrink-0 rounded-full"
                    style={{ backgroundColor: group.color }}
                  />
                )}
                <span className="truncate uppercase">{group.label}</span>
              </span>
              <span className="shrink-0 tabular font-normal text-text-tertiary">
                {group.meta}
              </span>
            </h2>
          )}

          <ul>
            {group.nodes.map((node) => (
              <TaskRow
                key={node.id}
                node={node}
                clientColor={colorFor(node.client_id)}
                timezone={timezone}
                onToggleDone={toggleDone}
                onOpen={(t) => setOpenTaskId(t.id)}
                showCompletedAt={view === 'completed'}
                completedHistory={view === 'completed'}
              />
            ))}
          </ul>
        </section>
      ))}

      <AnimatePresence>
        {openNode && (
          <TaskDetail
            task={openNode}
            onClose={() => setOpenTaskId(null)}
            onOpenTask={setOpenTaskId}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function ViewChip({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'pressable inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-small transition-colors duration-press',
        active
          ? 'bg-accent text-text-on-accent'
          : 'bg-surface-2 text-text-secondary hover:bg-surface-3 hover:text-text',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function FilterChip({
  active,
  onClick,
  dot,
  count,
  children,
}: {
  active: boolean;
  onClick: () => void;
  dot?: string;
  count?: number;
  children: React.ReactNode;
}) {
  const empty = count === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'pressable inline-flex min-h-touch items-center gap-2 rounded-md px-3 text-small',
        'whitespace-nowrap transition-colors duration-press',
        active
          ? 'bg-accent text-text-on-accent'
          : 'bg-surface-2 text-text-secondary hover:bg-surface-3 hover:text-text',
        // Still reachable, just not competing with the chips that hold work.
        !active && empty && 'text-text-tertiary opacity-60',
      )}
    >
      {dot && (
        <span
          aria-hidden
          className="size-dot shrink-0 rounded-full"
          style={{ backgroundColor: dot }}
        />
      )}
      {children}
      {count !== undefined && count > 0 && (
        <span
          className={cn(
            'tabular text-label',
            active ? 'opacity-70' : 'text-text-tertiary',
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function EmptyState({
  view,
  quickWins,
  clientFiltered,
  onClear,
}: {
  view: BacklogView;
  quickWins: boolean;
  clientFiltered: boolean;
  onClear: () => void;
}) {
  const filtered = quickWins || clientFiltered;
  const showClear = filtered;
  const title =
    view === 'completed'
      ? 'No completed tasks yet'
      : view === 'scheduled'
        ? 'Nothing scheduled'
        : quickWins
          ? 'No quick wins right now'
          : 'Nothing in the backlog';
  const detail =
    view === 'completed'
      ? clientFiltered
        ? 'Nothing completed for this client. Clear the filter to see everything.'
        : 'Finished work shows up here so you can review notes and time logged later.'
      : view === 'scheduled'
        ? clientFiltered
          ? 'Nothing scheduled for this client. Clear the filter to see everything.'
          : 'Tasks on the plan appear here until they are finished or returned to the backlog.'
        : quickWins
          ? 'Nothing here is under 15 minutes. Clear the filter to see everything.'
          : clientFiltered
            ? 'Nothing for this client. Clear the filter to see everything.'
            : 'Capture something and it will show up here.';
  return (
    // An empty state names the next action rather than just reporting absence.
    <div className="flex flex-col items-start gap-3 rounded-lg border border-border bg-surface-1 p-5">
      <Inbox size={20} strokeWidth={1.5} className="text-text-tertiary" aria-hidden />
      <div>
        <p className="text-body font-medium">{title}</p>
        <p className="mt-1 text-small text-text-secondary">{detail}</p>
      </div>
      {showClear && (
        <Button variant="secondary" onClick={onClear}>
          Clear filters
        </Button>
      )}
    </div>
  );
}
